import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { exportExcelSection, exportPDFSection } from '../common/ExportUtils';
import { openFilePreview } from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';
import TableScrollContainer from '../common/TableScrollContainer';
import { getAuthHeaders } from '../../auth';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';

const API_BASE = '/api/documentation/jobs';

const fundRequestIcon = (r, released) => r.is_fund_request && !r.fund_request_released && !released && (r.border_remarks || '').toUpperCase() !== 'RELEASED' ? (
  <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />
) : null;

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const BORDER_POINT_OPTIONS = [
  { id: 'DONE', text: 'DONE' },
  { id: 'NOT DONE', text: 'NOT DONE' },
  { id: 'N/A', text: 'N/A' },
];

const RELEASE_ORDER_OPTIONS = [
  { id: 'OBTAINED', text: 'OBTAINED' },
  { id: 'NOT OBTAINED', text: 'NOT OBTAINED' },
  { id: 'N/A', text: 'N/A' },
];

const BorderMainTab = ({ hasPermission, fixedRemarkFilter, onOpenDailyReports, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idfFilter, setIdfFilter] = useState('');
  const [search, setSearch] = useState('');
  const [remarkFilter, setRemarkFilter] = useState(fixedRemarkFilter || '');
  const [dropdowns, setDropdowns] = useState({ remarks: [] });
  const [savingId, setSavingId] = useState(null);
  const [generatingDaily, setGeneratingDaily] = useState(false);
  const [previewingDaily, setPreviewingDaily] = useState(false);
  const [editingReleasedRemarkId, setEditingReleasedRemarkId] = useState(null);
  const [tempReleasedRemark, setTempReleasedRemark] = useState('');
  const [tempReleasedDate, setTempReleasedDate] = useState('');
  const [drafts, setDrafts] = useState({});
  const [editingCells, setEditingCells] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [sortConfig, setSortConfig] = useState(null);

  // Drag-and-drop state
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const isReleasedReport = (fixedRemarkFilter || '').toUpperCase() === 'RELEASED';

  const fetchDropdowns = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/border-dropdowns/`, { headers: getAuthHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      setDropdowns({ remarks: data.remarks || [] });
    } catch {
      // Non-blocking fetch.
    }
  }, []);

  const fetchRowsForRemarkFilter = useCallback(async (nextRemarkFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextRemarkFilter) params.append('remark_filter', nextRemarkFilter);
      const res = await fetch(`${API_BASE}/border-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch border report.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch border report.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    await fetchRowsForRemarkFilter(remarkFilter);
  }, [fetchRowsForRemarkFilter, remarkFilter]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (isReleasedReport) {
      setSortConfig({ key: 'release_date', direction: 'desc' });
    } else {
      setSortConfig(null);
    }
  }, [isReleasedReport]);

  // Refresh when refreshKey changes (tab click)
  useEffect(() => {
    if (refreshKey !== undefined) {
      fetchRows();
    }
  }, [refreshKey, fetchRows]);


  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        next[r.id] = {
          ...(next[r.id] || {}),
          eta: r.eta || '',
          borderPointConfirmation: r.border_point_confirmation || '',
          releaseOrder: r.release_order || '',
          releaseOrderFile: null,
          remarks: r.raw_remarks || '',
          releaseDate: r.release_date || '',
        };
      });
      return next;
    });
  }, [rows]);

  const getCellKey = (rowId, field) => `${rowId}:${field}`;

  const isEditingCell = (rowId, field) => !!editingCells[getCellKey(rowId, field)];

  const startEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        borderPointConfirmation: row.border_point_confirmation || '',
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
        dutyPaid: row.duty_paid || false,
      },
    }));
    setEditingCells(prev => ({ ...prev, [getCellKey(row.id, field)]: true }));
  };

  const cancelEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        borderPointConfirmation: row.border_point_confirmation || '',
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
        dutyPaid: row.duty_paid || false,
      },
    }));
    setEditingCells(prev => {
      const next = { ...prev };
      delete next[getCellKey(row.id, field)];
      return next;
    });
  };

  const saveEditCell = async (row, field, payload) => {
    const saved = await updateJobField(row, payload);
    if (saved) {
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, field)];
        return next;
      });
    }
  };

  const handleSaveReleaseOrder = async (row) => {
    if (!hasPermission('BORDER REPORTS', 'can_edit')) {
      toast.error('You do not have permission to edit the Border report.');
      return;
    }
    const draft = drafts[row.id] || {};
    const value = (draft.releaseOrder || '').toUpperCase();
    const existingDoc = row.release_order_document;
    if (value === 'OBTAINED' && !existingDoc && !draft.releaseOrderFile) {
      toast.error('Please upload a Release Order document.');
      return;
    }
    setSavingId(row.id);
    try {
      let uploadResult = existingDoc || null;
      if (value === 'OBTAINED' && draft.releaseOrderFile) {
        const formData = new FormData();
        formData.append('document_type', 'Release Order');
        formData.append('document_file', draft.releaseOrderFile);
        const res = await fetch(`${API_BASE}/${row.id}/upload-job-document/?module=BORDER%20REPORTS`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to upload Release Order document.');
        uploadResult = data;
      }
      const patchRes = await fetch(`${API_BASE}/${row.id}/?module=BORDER%20REPORTS`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ border_release_order: draft.releaseOrder || null }),
      });
      if (!patchRes.ok) throw new Error('Failed to update release order.');
      await fetchRows();
      await fetchDropdowns();
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, 'releaseOrder')];
        return next;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to save release order.');
    } finally {
      setSavingId(null);
    }
  };

  // --- Drag-and-drop handlers ---
  const handleDragStart = useCallback((e, jobId) => {
    setDragSrcId(jobId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, jobId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (jobId !== dragSrcId) setDragOverId(jobId);
  }, [dragSrcId]);

  const handleDragEnd = useCallback(() => {
    setDragSrcId(null);
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(async (e, targetId) => {
    e.preventDefault();
    if (!dragSrcId || dragSrcId === targetId) { handleDragEnd(); return; }
    const newRows = [...rows];
    const srcIdx = newRows.findIndex(r => r.id === dragSrcId);
    const tgtIdx = newRows.findIndex(r => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) { handleDragEnd(); return; }
    const movingDown = srcIdx < tgtIdx;
    const [srcRow] = newRows.splice(srcIdx, 1);
    const newTgtIdx = newRows.findIndex(r => r.id === targetId);
    newRows.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, srcRow);
    setRows(newRows);
    handleDragEnd();
    const orders = newRows.map((r, i) => ({ id: r.id, sort_order: i }));
    try {
      const res = await fetch(`${API_BASE}/update-border-sort-order/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) throw new Error('Failed to save order.');
    } catch {
      toast.error('Failed to save row order.');
      fetchRows();
    }
  }, [dragSrcId, rows, setRows, handleDragEnd, fetchRows]);

  const clearAllFilters = async () => {
    setIdfFilter('');
    setSearch('');
    const nextRemark = fixedRemarkFilter || '';
    setRemarkFilter(nextRemark);
    await fetchRowsForRemarkFilter(nextRemark);
  };

  const updateJobField = async (row, payload) => {
    if (!hasPermission('BORDER REPORTS', 'can_edit')) {
      toast.error('You do not have permission to edit Border Report.');
      return false;
    }

    setSavingId(row.id);
    try {
      const res = await fetch(`${API_BASE}/${row.id}/?module=BORDER%20REPORTS`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update record.');
      await fetchRows();
      await fetchDropdowns();
      return true;
    } catch (e) {
      toast.error(e.message || 'Failed to update record.');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const toUpperDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value.toString().toUpperCase();
  };

  const handlePreviewDailyReport = async () => {
    setPreviewingDaily(true);
    try {
      const res = await fetch(`${API_BASE}/preview-daily-border-report/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate preview.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('The preview PDF is empty.');
      const blobUrl = URL.createObjectURL(blob);
      openFilePreview(blobUrl, `Preview_Border_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to generate preview.');
    } finally {
      setPreviewingDaily(false);
    }
  };

  const executeGenerateDailyReport = async () => {
    setGeneratingDaily(true);
    try {
      setIdfFilter('');
      setSearch('');
      setRemarkFilter('');
      await fetchRowsForRemarkFilter('');

      const res = await fetch(`${API_BASE}/generate-daily-border-report/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ report_date: new Date().toISOString().split('T')[0] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate daily border report.');
      }
      toast.success('Daily Border Report generated and saved successfully.');
      if (onOpenDailyReports) onOpenDailyReports();
    } catch (e) {
      toast.error(e.message || 'Failed to generate daily border report.');
    } finally {
      setGeneratingDaily(false);
    }
  };

  const handleGenerateDailyReport = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Generate Daily Report',
      message: 'Generating this report will overwrite any previously generated report for today. Are you sure you would like to generate this report now?',
      onConfirm: () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        executeGenerateDailyReport();
      },
      onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
    });
  };

  const filteredRows = useMemo(() => {
    let data = [...rows];
    if (idfFilter.trim()) {
      const needle = idfFilter.trim().toUpperCase();
      data = data.filter(r => (r.idf_no || '').toUpperCase().includes(needle));
    }
    if (search.trim()) {
      const needle = search.trim().toUpperCase();
      data = data.filter(r =>
        [
          r.idf_no,
          r.tansad_no,
          r.rcn_number,
          r.vehicle_no,
          r.eta,
          r.importer,
          r.border,
          r.border_point_confirmation,
          r.release_order,
          r.remarks,
        ].some(field => field && field.toString().toUpperCase().includes(needle))
      );
    }
    return data;
  }, [rows, idfFilter, search]);

  const acceptedRows = filteredRows
    .filter(r => ['ACCEPTED', 'SENT'].includes((r.assessment_status || '').toUpperCase()));

  const pendingRows = filteredRows
    .filter(r => !['ACCEPTED', 'SENT'].includes((r.assessment_status || '').toUpperCase()));

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const releasedSortedRows = useMemo(() => {
    if (!isReleasedReport || !sortConfig) return filteredRows;
    const data = [...filteredRows];
    const getVal = (r) => {
      const v = r[sortConfig.key];
      if (v === null || v === undefined || v === '') return '';
      return v.toString().trim().toUpperCase();
    };
    data.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredRows, sortConfig, isReleasedReport]);

  const remarkOptions = useMemo(() => {
    const values = dropdowns.remarks.map(r => (typeof r === 'string' ? r : r.text)).filter(Boolean);
    if (!values.includes('RELEASED')) values.push('RELEASED');
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.remarks]);

  const getRemarkOptionsForRow = (row) => {
    const isDutyPaid = (row.duty_status || '').toUpperCase() === 'PAID';
    const isExportRoad = (row.shipment_type || '').toUpperCase() === 'EXPORT-ROAD';
    return remarkOptions.filter(opt => opt.id !== 'RELEASED' || isDutyPaid || isExportRoad);
  };

  const normalizeCreatedValue = (term) => {
    const value = (term || '').trim();
    if (!value) return null;
    return { id: value, text: value };
  };

  const createRemarkOption = async (term) => {
    const option = normalizeCreatedValue(term);
    if (!option) return null;
    setDropdowns(prev => ({ ...prev, remarks: [...prev.remarks, option] }));
    return option;
  };

  const startEditReleasedRemark = (row, focusDate = false) => {
    setEditingReleasedRemarkId(row.id);
    setTempReleasedRemark(row.raw_remarks || '');
    setTempReleasedDate(row.release_date || '');
    if (focusDate) {
      setTimeout(() => {
        const el = document.querySelector(`input[data-release-date-for="${row.id}"]`);
        if (el) el.focus();
      }, 50);
    }
  };

  const cancelEditReleasedRemark = () => {
    setEditingReleasedRemarkId(null);
    setTempReleasedRemark('');
    setTempReleasedDate('');
  };

  const saveReleasedRemark = async (row) => {
    if (!hasPermission('BORDER REPORTS', 'can_delete')) return;
    const nextRemark = (tempReleasedRemark || '').trim();
    if (nextRemark === 'RELEASED' && !tempReleasedDate) {
      toast.error('Please enter a Release Date before saving RELEASED.');
      return;
    }
    if (nextRemark === 'RELEASED' && row.is_fund_request && !row.fund_request_released) {
      toast.error('Cannot Release. Awaiting Funds from Client');
      return;
    }
    const payload = { border_remarks: nextRemark || null };
    if (nextRemark === 'RELEASED' && tempReleasedDate) payload.release_date = tempReleasedDate;
    await updateJobField(row, payload);
    cancelEditReleasedRemark();
  };

  const getPendingSectionRemark = (r) => {
    const jobStatus = Array.isArray(r.job_status) ? r.job_status : [];
    if (jobStatus.some(s => String(s).toUpperCase() === 'COMPLETE')) {
      return r.assessment_status_label || r.assessment_status || '';
    }
    return r.status_remark || '';
  };

  const renderRows = (dataRows, options = {}) => {
    const { readOnly = false, remarksButtonOnly = false, pendingSection = false, onSort, sortConfig } = options;
    const baseColSpan = pendingSection ? 8 : 11;
    const colSpan = baseColSpan + (remarksButtonOnly ? 1 : 0);

    const sortableHeader = (label, key, extraStyle = {}) => (
      <th
        style={{ cursor: onSort ? 'pointer' : undefined, ...extraStyle }}
        onClick={onSort ? () => onSort(key) : undefined}
      >
        {label}
        {sortConfig?.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );

    return (
      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              {sortableHeader('IDF NO', 'idf_no')}
              {sortableHeader('TANSAD NO', 'tansad_no')}
              {sortableHeader('RCN NUMBER', 'rcn_number')}
              {sortableHeader('VEHICLE NO', 'vehicle_no')}
              {sortableHeader('ETA', 'eta')}
              {sortableHeader('IMPORTER', 'importer')}
              {!pendingSection && sortableHeader('DUTY PAID', 'duty_status')}
              {sortableHeader('BORDER', 'border')}
              {!pendingSection && sortableHeader('BORDER POINT CONFIRMATION', 'border_point_confirmation')}
              {!pendingSection && sortableHeader('RELEASE ORDER', 'release_order')}
              {sortableHeader('REMARKS', 'remarks')}
              {remarksButtonOnly && sortableHeader('RELEASED DATE', 'release_date', { whiteSpace: 'nowrap' })}
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && dataRows.length === 0 && (<tr><td colSpan={colSpan} className="no-data">No records found.</td></tr>)}
            {!loading && dataRows.map(r => (
              <tr
                key={r.id}
                draggable={hasPermission('BORDER REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly}
                onDragStart={hasPermission('BORDER REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly ? (e) => handleDragStart(e, r.id) : undefined}
                onDragOver={(e) => handleDragOver(e, r.id)}
                onDrop={(e) => handleDrop(e, r.id)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: hasPermission('BORDER REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly ? 'grab' : undefined,
                  ...(dragOverId === r.id && dragSrcId !== r.id ? { outline: '2px dashed #3f6b32', backgroundColor: '#e8f5e9' } : {}),
                  ...(dragSrcId === r.id ? { opacity: 0.5 } : {}),
                }}
              >
                <td style={{ userSelect: 'text' }}><CopyCell text={r.idf_no}>{toUpperDisplay(r.idf_no)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.tansad_no}>{toUpperDisplay(r.tansad_no)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.rcn_number}>{toUpperDisplay(r.rcn_number)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.vehicle_no}>{toUpperDisplay(r.vehicle_no)}</CopyCell></td>
                <td>
                  {!readOnly && hasPermission('BORDER REPORTS', 'can_edit') ? isEditingCell(r.id, 'eta') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="date"
                        className="search-input"
                        value={drafts[r.id]?.eta || ''}
                        onChange={e => setDrafts(prev => ({
                          ...prev,
                          [r.id]: { ...(prev[r.id] || {}), eta: e.target.value },
                        }))}
                        disabled={savingId === r.id}
                        style={{ minWidth: 145 }}
                      />
                      <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'eta', { airport_eta: (drafts[r.id]?.eta || '').trim() || null })}><i className="fa fa-save" /></button>
                      <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'eta')}><i className="fa fa-times" /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.eta}><span>{toUpperDisplay(r.eta)}</span></CopyCell>
                      <button type="button" className="icon-btn" title="Edit ETA" onClick={() => startEditCell(r, 'eta')}><i className="fa fa-pencil" /></button>
                    </div>
                  ) : (
                    <CopyCell text={r.eta}>{toUpperDisplay(r.eta)}</CopyCell>
                  )}
                </td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.importer}><span><strong>{toUpperDisplay(r.importer)}</strong>{!isReleasedReport && r.is_fund_request && !r.fund_request_released && (r.border_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</span></CopyCell></td>
                {!pendingSection && (
                <td style={{ userSelect: 'text' }}>
                  {!readOnly && hasPermission('BORDER REPORTS', 'can_edit') ? (
                    <input
                      type="checkbox"
                      checked={!!r.duty_paid}
                      disabled={savingId === r.id}
                      onChange={e => saveEditCell(r, 'dutyPaid', { duty_paid: e.target.checked })}
                    />
                  ) : (
                    <CopyCell text={r.duty_status}>{toUpperDisplay(r.duty_status)}</CopyCell>
                  )}
                </td>
                )}
                <td style={{ userSelect: 'text' }}><CopyCell text={r.border}>{toUpperDisplay(r.border)}</CopyCell></td>
                {!pendingSection && (
                <td>
                  {!readOnly && hasPermission('BORDER REPORTS', 'can_edit') ? isEditingCell(r.id, 'borderPoint') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Select2Dropdown
                        options={BORDER_POINT_OPTIONS}
                        value={drafts[r.id]?.borderPointConfirmation || ''}
                        onChange={val => setDrafts(prev => ({
                          ...prev,
                          [r.id]: { ...(prev[r.id] || {}), borderPointConfirmation: val || '' },
                        }))}
                        valueKey="id"
                        displayKey="text"
                        placeholder="Select"
                        clearable
                        style={{ minWidth: 180 }}
                        disabled={savingId === r.id}
                      />
                      <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'borderPoint', { border_point_confirmation: drafts[r.id]?.borderPointConfirmation || null })}><i className="fa fa-save" /></button>
                      <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'borderPoint')}><i className="fa fa-times" /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.border_point_confirmation}><span>{toUpperDisplay(r.border_point_confirmation)}</span></CopyCell>
                      <button type="button" className="icon-btn" title="Edit Border Point Confirmation" onClick={() => startEditCell(r, 'borderPoint')}><i className="fa fa-pencil" /></button>
                    </div>
                  ) : (
                    <CopyCell text={r.border_point_confirmation}>{toUpperDisplay(r.border_point_confirmation)}</CopyCell>
                  )}
                </td>
                )}
                {!pendingSection && (
                <td>
                  {!readOnly && hasPermission('BORDER REPORTS', 'can_edit') ? isEditingCell(r.id, 'releaseOrder') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <Select2Dropdown
                        options={RELEASE_ORDER_OPTIONS}
                        value={drafts[r.id]?.releaseOrder || ''}
                        onChange={val => setDrafts(prev => ({
                          ...prev,
                          [r.id]: { ...(prev[r.id] || {}), releaseOrder: val || '', releaseOrderFile: null },
                        }))}
                        valueKey="id"
                        displayKey="text"
                        placeholder="Select"
                        clearable
                        style={{ minWidth: 170 }}
                        disabled={savingId === r.id}
                      />
                      {drafts[r.id]?.releaseOrder === 'OBTAINED' && (
                        <>
                          <input
                            type="file"
                            id={`release-order-file-${r.id}`}
                            accept=".pdf,.png,.jpg,.jpeg"
                            style={{ display: 'none' }}
                            onChange={e => {
                              const file = e.target.files?.[0] || null;
                              setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseOrderFile: file } }));
                            }}
                          />
                          <label htmlFor={`release-order-file-${r.id}`} className="icon-btn" title="Upload Release Order" style={{ cursor: 'pointer' }}>
                            <i className="fa fa-upload" />
                          </label>
                          {drafts[r.id]?.releaseOrderFile && (
                            <span style={{ fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={drafts[r.id].releaseOrderFile.name}>{drafts[r.id].releaseOrderFile.name}</span>
                          )}
                        </>
                      )}
                      {r.release_order_document && (
                        <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                      )}
                      <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => handleSaveReleaseOrder(r)}><i className="fa fa-save" /></button>
                      <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'releaseOrder')}><i className="fa fa-times" /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.release_order}><span>{toUpperDisplay(r.release_order)}</span></CopyCell>
                      {r.release_order_document && (
                        <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                      )}
                      <button type="button" className="icon-btn" title="Edit Release Order" onClick={() => startEditCell(r, 'releaseOrder')}><i className="fa fa-pencil" /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.release_order}>{toUpperDisplay(r.release_order)}</CopyCell>
                      {r.release_order_document && (
                        <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                      )}
                    </div>
                  )}
                </td>
                )}
                <td style={{ minWidth: 220 }}>
                  {pendingSection ? (
                    <CopyCell text={getPendingSectionRemark(r)}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(getPendingSectionRemark(r))}</span></CopyCell>
                  ) : remarksButtonOnly ? (
                    editingReleasedRemarkId === r.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Select2Dropdown
                              options={getRemarkOptionsForRow(r)}
                              value={tempReleasedRemark}
                              onChange={v => { setTempReleasedRemark(v); if (v !== 'RELEASED') setTempReleasedDate(''); }}
                              valueKey="id"
                              displayKey="text"
                              placeholder="SELECT REMARK"
                              clearable
                              style={{ width: '100%' }}
                              disabled={savingId === r.id}
                            />
                          </div>
                          <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveReleasedRemark(r)}><i className="fa fa-save" /></button>
                          <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditReleasedRemark}><i className="fa fa-times" /></button>
                        </div>
                        {tempReleasedRemark === 'RELEASED' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                            <input
                              type="date"
                              className="form-control"
                              style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                              value={tempReleasedDate}
                              onChange={e => setTempReleasedDate(e.target.value)}
                              required
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CopyCell text={r.remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</span></CopyCell>
                        {hasPermission('BORDER REPORTS', 'can_delete') && (
                          <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditReleasedRemark(r)}><i className="fa fa-pencil" /></button>
                        )}
                      </div>
                    )
                  ) : !readOnly && hasPermission('BORDER REPORTS', 'can_edit') ? isEditingCell(r.id, 'remarks') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select2Dropdown
                            options={getRemarkOptionsForRow(r)}
                            value={drafts[r.id]?.remarks || ''}
                            onChange={val => setDrafts(prev => ({
                              ...prev,
                              [r.id]: { ...(prev[r.id] || {}), remarks: val || '', releaseDate: val !== 'RELEASED' ? '' : (prev[r.id]?.releaseDate || '') },
                            }))}
                            valueKey="id"
                            displayKey="text"
                            placeholder="SELECT REMARK"
                            clearable
                            style={{ width: '100%' }}
                            disabled={savingId === r.id}
                          />
                        </div>
                        <button
                          className="icon-btn save-btn"
                          title="Save"
                          disabled={savingId === r.id}
                          onClick={() => {
                            const remark = drafts[r.id]?.remarks || '';
                            if (remark === 'RELEASED' && !(drafts[r.id]?.releaseDate)) {
                              toast.error('Please enter a Release Date before saving RELEASED.');
                              return;
                            }
                            if (remark === 'RELEASED' && r.is_fund_request && !r.fund_request_released) {
                              toast.error('Cannot Release. Awaiting Funds from Client');
                              return;
                            }
                            const payload = { border_remarks: remark || null };
                            if (remark === 'RELEASED' && drafts[r.id]?.releaseDate) payload.release_date = drafts[r.id].releaseDate;
                            saveEditCell(r, 'remarks', payload);
                          }}
                        ><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'remarks')}><i className="fa fa-times" /></button>
                      </div>
                      {(drafts[r.id]?.remarks || '') === 'RELEASED' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                            value={drafts[r.id]?.releaseDate || ''}
                            onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseDate: e.target.value } }))}
                            required
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</span></CopyCell>
                      <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditCell(r, 'remarks')}><i className="fa fa-pencil" /></button>
                    </div>
                  ) : (
                    <CopyCell text={r.remarks}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</CopyCell>
                  )}
                </td>
                {remarksButtonOnly && (
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600, userSelect: 'text' }}>
                    {editingReleasedRemarkId === r.id ? (
                      <input
                        data-release-date-for={r.id}
                        type="date"
                        className="form-control"
                        style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                        value={tempReleasedDate}
                        onChange={e => setTempReleasedDate(e.target.value)}
                      />
                    ) : (
                      <CopyCell text={r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : ''}>
                        {r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '—'}
                      </CopyCell>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    );
  };

  return (
    <>
      <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto' }}>
          <input className="search-input" placeholder="IDF Search" value={idfFilter} onChange={e => setIdfFilter(e.target.value)} style={{ minWidth: 140 }} />
          {!isReleasedReport && (
            <Select2Dropdown
              options={[{ id: '', text: 'All Remarks' }, ...remarkOptions]}
              value={remarkFilter}
              onChange={val => setRemarkFilter(val || '')}
              valueKey="id"
              displayKey="text"
              style={{ minWidth: 180 }}
              placeholder="Remarks Filter"
              isDisabled={!!fixedRemarkFilter}
            />
          )}
          <input className="search-input" placeholder="General Search" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 260 }} />
          <button className="add-btn" style={{ background: '#6c757d', whiteSpace: 'nowrap' }} onClick={clearAllFilters}>Clear All Filters</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="add-btn" onClick={fetchRows}>Refresh</button>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!fixedRemarkFilter && (
              <>
                <button className="add-btn" style={{ background: '#e67e22' }} onClick={handlePreviewDailyReport} disabled={previewingDaily || generatingDaily}>
                  {previewingDaily ? 'Loading Preview...' : 'Preview Daily Report'}
                </button>
                <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleGenerateDailyReport} disabled={generatingDaily || previewingDaily}>
                  {generatingDaily ? 'Generating...' : 'Generate Daily Report'}
                </button>
              </>
            )}
            <button className="add-btn" style={{ background: '#17a2b8' }} onClick={() => exportPDFSection(filteredRows, 'BorderReport_All', { excludeColumns: ['duty_paid', 'duty_status'] })}>Download PDF</button>
            <button className="add-btn" style={{ background: '#007bff' }} onClick={() => exportExcelSection(filteredRows, 'BorderReport_All', { excludeColumns: ['duty_paid', 'duty_status'] })}>Download Excel</button>
          </div>
        </div>
      </div>

      {!fixedRemarkFilter && (
        <>
          <div style={{ marginBottom: 26 }}>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#d9ecff', borderRadius: 6, padding: 8, fontSize: 16 }}>
              BORDER REPORT - ASSESSMENT ACCEPTED / SENT
            </div>
            {renderRows(acceptedRows)}
          </div>

          <div>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#ffe3bf', borderRadius: 6, padding: 8, fontSize: 16 }}>
              PENDING ASSESSMENTS
            </div>
            {renderRows(pendingRows, { pendingSection: true })}
          </div>
        </>
      )}

      {fixedRemarkFilter && (
        <div>
          <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2', borderRadius: 6, padding: 8, fontSize: 16 }}>
            RELEASED REPORT
          </div>
          {renderRows(releasedSortedRows, { readOnly: true, remarksButtonOnly: true, onSort: handleSort, sortConfig })}
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />
    </>
  );
};

function formatDailyDate(dateStr, createdAt) {
  const pad = n => n.toString().padStart(2, '0');
  let datePart = dateStr || '';
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d)) datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  let timePart = '';
  if (createdAt) {
    const t = new Date(createdAt);
    if (!isNaN(t)) timePart = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
  }
  return { datePart, timePart };
}

const DailyBorderReportsTab = ({ hasPermission }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const toUpperDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value.toString().toUpperCase();
  };

  const handleViewPdf = useCallback(async (row) => {
    try {
      const res = await fetch(row.view_pdf_url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load PDF preview.');

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error('The generated PDF file is empty.');

      const blobUrl = URL.createObjectURL(blob);
      const previewWindow = openFilePreview(blobUrl, `Daily_Border_Report_${row.date}.pdf`);
      if (previewWindow && previewWindow.addEventListener) {
        previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
      }
    } catch (e) {
      toast.error(e.message || 'Could not open PDF preview.');
    }
  }, []);

  const handleDownload = useCallback(async (url, filename) => {
    try {
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('Downloaded file is empty.');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e.message || 'Download failed.');
    }
  }, []);

  const fetchDailyReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (singleDate) {
        params.append('date', singleDate);
      } else {
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
      }
      const res = await fetch(`${API_BASE}/daily-border-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch daily border reports.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch daily border reports.');
    } finally {
      setLoading(false);
    }
  }, [singleDate, fromDate, toDate]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);


  if (!hasPermission('BORDER REPORTS', 'can_view')) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Daily Border Reports.</p></div>;
  }

  return (
    <div>
      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Single Date</label>
          <input type="date" className="search-input" value={singleDate} onChange={e => setSingleDate(e.target.value)} title="Single Date" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Date Range</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" className="search-input" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From Date" />
            <span style={{ color: '#666', fontWeight: 700 }}>To</span>
            <input type="date" className="search-input" value={toDate} onChange={e => setToDate(e.target.value)} title="To Date" />
          </div>
        </div>
        <button className="add-btn" onClick={fetchDailyReports}>Apply Date Filter</button>
        <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setSingleDate(''); setFromDate(''); setToDate(''); }}>Clear Date Filter</button>
      </div>

      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>DATE</th>
              <th>GENERATED BY</th>
              <th>VIEW PDF</th>
              <th>DOWNLOAD PDF</th>
              <th>DOWNLOAD EXCEL</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={5} className="no-data">No daily reports found.</td></tr>)}
            {!loading && rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => { const { datePart, timePart } = formatDailyDate(r.date, r.created_at); return (<span>{datePart}{timePart && <><br /><span style={{ fontSize: 11, color: '#555' }}>{timePart}</span></>}</span>); })()}
                </td>
                <td>{toUpperDisplay(r.generated_by)}</td>
                <td>
                  <button type="button" className="icon-btn" title="View PDF" onClick={() => handleViewPdf(r)}>
                    <i className="fa fa-eye" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download PDF" onClick={() => handleDownload(r.download_pdf_url, `Daily_Border_Report_${r.date}.pdf`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download Excel" onClick={() => handleDownload(r.download_excel_url, `Daily_Border_Report_${r.date}.xlsx`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    </div>
  );
};

export default function BorderReport() {
  const { hasPermission: rawHasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('borderReport');
  const [refreshKey, setRefreshKey] = useState(0);

  const hasPermission = useCallback((module, action) => {
    if (module === 'BORDER REPORTS' || module === 'BORDER REPORT' || module === 'PORT BORDER REPORT') {
      return rawHasPermission('BORDER REPORT', action)
        || rawHasPermission('PORT BORDER REPORT', action)
        || rawHasPermission('BORDER REPORTS', action)
        || rawHasPermission('ADMIN BORDER REPORTS', action);
    }
    return rawHasPermission(module, action);
  }, [rawHasPermission]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (!hasPermission('BORDER REPORTS', 'can_view')) {
      return;
    }
    if (!['borderReport', 'releasedReport', 'dailyBorderReports'].includes(activeTab)) {
      setActiveTab('borderReport');
    }
  }, [activeTab, hasPermission]);

  if (!hasPermission('BORDER REPORTS', 'can_view')) {
    return (
      <div className="permission-denied-container" style={{ padding: '20px' }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view Border Reports.</p>
      </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Border Reports</h2>
        <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
        <button className={`custom-tab ${activeTab === 'borderReport' ? 'active' : ''}`} onClick={() => { setActiveTab('borderReport'); handleRefresh(); }}>
          Border Report
        </button>
        <button className={`custom-tab ${activeTab === 'dailyBorderReports' ? 'active' : ''}`} onClick={() => setActiveTab('dailyBorderReports')}>
          Daily Border Report
        </button>
        <button className={`custom-tab ${activeTab === 'releasedReport' ? 'active' : ''}`} onClick={() => { setActiveTab('releasedReport'); handleRefresh(); }}>
          Released Report
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'borderReport' && (
          <BorderMainTab
            hasPermission={hasPermission}
            onOpenDailyReports={() => setActiveTab('dailyBorderReports')}
            refreshKey={refreshKey}
          />
        )}

        {activeTab === 'dailyBorderReports' && (
          <DailyBorderReportsTab hasPermission={hasPermission} />
        )}

        {activeTab === 'releasedReport' && (
          <BorderMainTab
            hasPermission={hasPermission}
            fixedRemarkFilter="RELEASED"
            onOpenDailyReports={() => setActiveTab('dailyBorderReports')}
            refreshKey={refreshKey}
          />
        )}
      </div>
    </div>
  );
}
